/** Reader events, batched to the API's events endpoint. Each carries a client ULID the server uses as an
 * idempotency key, so retries (offline, a page closed mid-flight) never double-count. The
 * buffer flushes every 5 s and when the page hides, with keepalive; unsent events wait in
 * localStorage for the next visit. The endpoint only accepts signed-in readers, so a 401
 * drops the batch rather than retrying forever. */
import { API_BASE } from "../api/client";

type EventType =
  | "story_opened"
  | "term_discovered"
  | "term_clicked"
  | "term_dismissed"
  | "link_followed"
  | "feedback";

interface Queued {
  id: string;
  type: EventType;
  term_id?: string;
  story_id?: string;
  payload: Record<string, unknown>;
  client_ts: string;
}

const STORAGE = "learn-events";
const FLUSH_MS = 5000;

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function encode(value: bigint, length: number): string {
  let out = "";
  for (let i = 0; i < length; i++) {
    out = CROCKFORD[Number(value & 31n)] + out;
    value >>= 5n;
  }
  return out;
}

/** A ULID the server's is_valid_id accepts: 10 time chars + 16 random chars, Crockford base32. */
function ulid(): string {
  let rand = 0n;
  for (const b of crypto.getRandomValues(new Uint8Array(10))) rand = (rand << 8n) | BigInt(b);
  return encode(BigInt(Date.now()), 10) + encode(rand, 16);
}

function loadQueue(): Queued[] {
  try {
    const raw = localStorage.getItem(STORAGE);
    return raw ? (JSON.parse(raw) as Queued[]) : [];
  } catch {
    return [];
  }
}

let queue: Queued[] = loadQueue();
let timer: number | undefined;
let inFlight = false;

function persist() {
  try {
    if (queue.length) localStorage.setItem(STORAGE, JSON.stringify(queue.slice(0, 200)));
    else localStorage.removeItem(STORAGE);
  } catch {
    /* private mode: events just live in memory */
  }
}

async function flush() {
  if (inFlight || queue.length === 0) return;
  inFlight = true;
  const batch = queue.slice(0, 50);
  try {
    const res = await fetch(`${API_BASE}/events`, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "Content-Type": "application/json", "X-Requested-With": "learn" },
      body: JSON.stringify({ events: batch }),
    });
    if (res.ok || res.status === 401 || res.status === 403) {
      queue = queue.slice(batch.length);
    }
    // Other failures keep the events for the next flush.
  } catch {
    /* offline: keep them */
  } finally {
    inFlight = false;
    persist();
    if (queue.length) schedule();
  }
}

function schedule() {
  timer ??= window.setTimeout(() => {
    timer = undefined;
    void flush();
  }, FLUSH_MS);
}

export function track(
  type: EventType,
  fields: { term_id?: string; story_id?: string; [key: string]: unknown } = {},
) {
  const { term_id, story_id, ...payload } = fields;
  queue.push({
    id: ulid(),
    type,
    term_id,
    story_id,
    payload,
    client_ts: new Date().toISOString(),
  });
  persist();
  schedule();
}

window.addEventListener("pagehide", () => void flush());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") void flush();
});
